import { maxInputDecimals } from './helpers';
import { viewRole } from './viewRole';

// Shared payment-task helpers so the pending-tasks pool card (PendingTaskList)
// and the Task Tracking claimed card can NEVER offer a different action set for
// the same task state. Prior to this file each surface computed "is there still
// something to prepare?" inline, and they drifted: the pool offered a "Prepare
// remaining" affordance the core Task Tracking module lacked. These are the ONE
// definitions both import.

import { formatCurrency, formatCryptoAmount } from './helpers';

// Allocated to prepared + completed tranches. Single source: task.paid_total
// (server-computed SUM over payment_request_payments / platform_order_payments).
export const taskAllocated = (task) => parseFloat(task?.paid_total || 0);

// "Fully prepared" = the whole requested amount is already allocated to
// prepared/completed tranches, so there is NOTHING left to prepare. This is the
// single gate that governs whether a "prepare / prepare remaining" affordance
// shows. When true, the only remaining work is completing the prepared tranches
// (Mark complete) — never preparing more — so the prepare button must hide on
// EVERY surface, or finance could double-pay.
export const taskFullyPrepared = (task) => fullyAllocated(task?.amount, taskAllocated(task));

// ---- REMAINING: ONE formula (audit T5 / A-6 / D-14, 2026-10-11). -----------
// Before this, "how much is left" had four hand-written copies — the pool card
// header (amount − prepared − completed), the copy button / payee block (the
// FULL amount, so a partly-prepared platform payment could be paid twice),
// Platform Management (fiat − completed only) and the order modal's summary
// (a toFixed(2) STRING compare where everything else uses 0.01 tolerance).
// Anything under a cent is "nothing left", the same 0.01 taskFullyPrepared and
// the server (fullyPaid = covered >= total - 0.01) use.
export const amountLeft = (total, allocated) => {
  const left = (parseFloat(total) || 0) - (parseFloat(allocated) || 0);
  return left > 0.01 ? left : 0;
};

// The same test on bare figures — for a screen that holds LIVE totals (the
// fulfil modal's paidTotal moves as slips are prepared / cancelled inside it)
// rather than a task field: something is allocated and nothing is left.
export const fullyAllocated = (total, allocated) =>
  (parseFloat(allocated) || 0) > 0 && amountLeft(total, allocated) === 0;

// What is still left to PAY (or, for a money-IN sell, to RECORD) on a payment /
// platform task: amount − prepared − completed. Derived live from the task's own
// fields, never a stored figure. This is the figure the card header says is
// "left", and the ONLY amount any copy button / payee block may hand to a bank
// app — copying the full amount made finance pay a part-paid request again
// (payment requests fixed 2026-07-23; platform payments 2026-10-11). For any
// other task type there is no progress to subtract: it is just the amount.
export const remainingToPay = (task) => {
  if (!task) return 0;
  if (task.task_type !== 'payment_request' && task.task_type !== 'platform_order') {
    return parseFloat(task.amount || 0);
  }
  return amountLeft(task.amount, taskAllocated(task));
};

// What is still left to CONFIRM: amount − COMPLETED only (a prepared/recorded
// slip has not moved money yet). Same formula, the narrower "allocated". The
// sell-side "link an existing credit" rule is this figure — the server computes
// fiat − completed for it — so the Platform Management screen must read this
// one, not remainingToPay, to match the server to the cent.
export const remainingToConfirm = (task) => amountLeft(task?.amount, task?.completed_total);

// Tiny adapter: a Platform Orders API order + its payments → the task-shaped
// fields the two helpers above read (amount, paid_total, completed_total), so
// Platform Management never carries a formula of its own.
export const taskFromPlatformOrder = (order, payments) => {
  const rows = Array.isArray(payments) ? payments : [];
  const sum = (keep) => rows.filter(keep).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  return {
    task_type: 'platform_order',
    amount: order ? order.fiat_amount : 0,
    paid_total: sum(() => true),
    completed_total: sum((p) => String(p.status) === 'completed'),
  };
};

// ---- P2P SELL: where the customer's money has got to — ONE wording ---------
// (operator 2026-10-11). The order panel used to say "Waiting on finance to
// record RM x" in TWO different situations: when nothing had been recorded yet,
// AND when finance had already RECORDED the credit against this order (a
// 'prepared' money-IN payment) and only their "Confirm received" step was left.
// Those are different asks of finance and must never read the same. Every
// surface that describes a sell's outstanding money builds its sentence(s)
// here, from the same task-shaped figures the two helpers above read (feed it
// taskFromPlatformOrder(order, payments)), so the wording cannot drift:
//   recorded  = prepared − completed   (recorded, not yet confirmed)
//   toRecord  = remainingToPay         (amount − prepared − completed)
// Returns { before, amount, after } lines (the caller bolds "RM amount"); an
// empty array when nothing is outstanding.
export const sellReceiveHintLines = (task) => {
  const lines = [];
  const recorded = amountLeft(taskAllocated(task), task?.completed_total);
  const toRecord = remainingToPay(task);
  if (recorded > 0) {
    lines.push({ before: '', amount: recorded, after: ' recorded against this order, waiting for finance to confirm.' });
  }
  if (toRecord > 0) {
    // Nothing recorded or confirmed yet → the whole order is waiting on finance;
    // otherwise only a remainder is unclaimed.
    lines.push(taskAllocated(task) <= 0.01
      ? { before: 'Waiting on finance to record ', amount: toRecord, after: '. If they have already recorded it off the bank statement, claim it here — no second request is raised.' }
      : { before: '', amount: toRecord, after: ' of this order is still unclaimed. Claim the rest, or leave it for finance to record.' });
  }
  return lines;
};

// ---- Platform-order money DIRECTION. The server derives platform_order_type
// from the order row (never a stored copy) and every surface keys off THIS
// helper, so no screen can offer "choose the account to deduct" on a trade
// where the customer is paying US. ----
export const isPlatformMoneyIn = (task) => task?.platform_order_type === 'p2p_sell';

// Label for the prepare button: first-time prepare vs topping up a partially
// prepared request/order. A money-IN platform sell is not a payment being
// prepared — it is a receipt being recorded.
export const prepareButtonLabel = (task) => {
  if (isPlatformMoneyIn(task)) {
    return taskAllocated(task) > 0 ? 'Record remaining received' : 'Record payment received';
  }
  return taskAllocated(task) > 0 ? 'Prepare remaining' : 'Upload receipt & prepare';
};

// (fullyPreparedPillText lived here. It keyed on paid_total only, so on a PAID
// order it still told finance to "mark complete in Task Tracking" — audit
// A-1/D-1. The pool pill now reads the ONE stage model, taskStepSummary()
// below, through <TaskProgressBar compact showHint />.)

// ---- Platform-order payee formatting. A platform order's payee bank details
// live on platform_orders.customer_bank_accounts, exposed as platform_payee_json:
// [{account_details:"NAME\nBANK\nACCOUNT", amount:"40880"}, ...]. Shared by the
// pool card's "Pay to" block/copy AND the fulfil modal's getPayeeText so the two
// can never render a different payee for the same order. ----
export const platformPayeeEntries = (task) => {
  try {
    const arr = task?.platform_payee_json ? JSON.parse(task.platform_payee_json) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) { return []; }
};

// A single payee mirrors payment_request exactly (details only; amount lives in
// the totals line); a P2P order with several payees labels each with its own
// amount so finance knows how much goes to which bank.
export const platformPayeeText = (task) => {
  // A P2P SELL has NO payee: the customer is paying US. Returning '' here is the
  // single gate that hides the "Pay to" / "Banking details" block and its copy
  // button on every surface at once (pool card + fulfil modal).
  if (isPlatformMoneyIn(task)) return '';
  let entries = platformPayeeEntries(task);
  // SPLIT p2p_buy (2026-08-18): a payment task owns ONE detail — show only
  // ITS payee, not the whole list (the other payments are other tasks).
  const seq = task?.platform_order_detail_seq;
  if (seq != null && entries.length >= seq) entries = [entries[seq - 1]];
  if (entries.length === 0) return (task?.description || '').toString();
  if (entries.length === 1) return String(entries[0].account_details || '').trim();
  return entries.map((en) => {
    const details = String(en.account_details || '').trim();
    const amt = parseFloat(en.amount || 0);
    return `${details}\n= RM ${amt.toFixed(2)}`;
  }).join('\n\n');
};

// Per-payee proof screenshots — "Payment proof (details shared at platform)",
// platform_order_details.proof_url — scoped to the card's OWN payment on a
// split buy, exactly like platformPayeeText above. Operator 2026-10-10: on the
// task cards these replace the platform trade screenshot, which was shown under
// the misleading label "Customer's payee proof" while the real payee proof was
// never rendered anywhere on a card (audit theme T6). Empty on a sell (no payee)
// and for a viewer whose payee details are masked.
export const platformPayeeProofs = (task) => {
  if (!task || isPlatformMoneyIn(task)) return [];
  let entries = platformPayeeEntries(task).map((en, i) => ({ seq: i + 1, url: en && en.proof_url ? String(en.proof_url) : '' }));
  const seq = task.platform_order_detail_seq;
  if (seq != null && entries.length >= seq) entries = [entries[seq - 1]];
  return entries.filter((e) => e.url);
};

// Copy text for the card's banking block: single payee → details + the amount
// LEFT to pay (identical to the payment_request card); multi-payee → the
// labelled block.
export const platformBankingDetailsText = (task) => {
  // Scope FIRST (split p2p_buy 2026-08-18): a payment-detail task owns one
  // payee, and its copy text must carry ITS amount — branching on the raw
  // list length dropped the amount from every split task's copy button.
  let entries = platformPayeeEntries(task);
  const seq = task?.platform_order_detail_seq;
  if (seq != null && entries.length >= seq) entries = [entries[seq - 1]];
  if (entries.length === 1) {
    // The REMAINDER, not the order amount: this is what the card header calls
    // "left", and a partly-prepared payment must never be copied at its full
    // figure (audit T5 / A-6 — the payment-request copy has done this since
    // 2026-07-23).
    return [String(entries[0].account_details || '').trim(), remainingToPay(task).toFixed(2)].join('\n');
  }
  return platformPayeeText(task);
};

// ===========================================================================
// PAYMENT-RECEIVED: what SALES asked for vs what FINANCE actually recorded.
//
// A payment_received task carries two different facts and they must never be
// confused on screen:
//   * the SALES submission — title / description / amount, a write-time copy of
//     what sales expected when they sent the receipt;
//   * the FINANCE record — recorded_description / recorded_amount, resolved LIVE
//     off the bank transaction by PAYMENT_RECEIVED_LIVE_RECORD_SQL.
//
// Once finance has recorded the credit, THEIRS is authoritative: they type what
// the bank statement shows, which on a third-party transfer is routinely a
// different name from the one sales expected. Sales used to see only their own
// copy and had no way to learn the real payer. These helpers are the ONE place
// that decides which fact wins, so every task surface answers it identically.
// A later edit to the tx description flows through automatically — that is the
// point of reading by reference.
// ===========================================================================
// BOTH FLOWS (operator re-report, 2026-08-02: "it happens on both normal order
// and platform order"). The first pass covered payment_received only. The
// NORMAL path is pr_notification — the "PR from <name>" card sales sees the
// moment finance records a credit off the bank statement. Its title and
// description are write-time COPIES scraped out of the transaction description,
// and they drift the instant anything re-describes that row (customer
// attribution suffix, internal-swap stamp, a plain typo correction). Measured on
// the prod copies: 16 of 20 such tasks on cnk, 3 of 6 on cedge. Both task types
// therefore resolve their payment text from the SOURCE ROW the task references.
const PR_LIVE_TASK_TYPES = new Set(['payment_received', 'pr_notification']);

// Does a live source row exist behind this task at all? recorded_amount is
// projected from the referenced transaction / crypto / blockchain row, and
// those columns are never NULL — so a value here PROVES the row resolved,
// independently of whether it happens to carry any description text.
export const prHasLiveRecord = (task) =>
  !!(task && PR_LIVE_TASK_TYPES.has(task.task_type) && task.recorded_amount != null);

export const prIsRecorded = (task) =>
  prHasLiveRecord(task) && String(task.recorded_description || '').trim() !== '';

// The authoritative payer/reference line, or null while finance has yet to record.
export const prRecordedName = (task) =>
  prIsRecorded(task) ? String(task.recorded_description || '').trim() || null : null;

// "Recorded by finance" is only honest when the live row is a BANK transaction
// somebody keyed in — that is the fact the operator asked to stop going stale.
// A crypto/blockchain source row was written by the monitor or by the flow that
// created it, so those cards keep the plain "Description" wording they have
// always had; only WHERE the text comes from changed (the row, not the copy).
// The server sets recorded_is_bank; this is the ONE place the wording is decided.
export const prRecordedLabel = (task) =>
  (task && Number(task.recorded_is_bank) === 1) ? 'Recorded by finance' : 'Description';

// Does this task carry a SECOND, independent human fact worth showing next to
// the live record? Only payment_received does: sales wrote a note explaining
// why they sent the receipt. A pr_notification's stored description is the same
// transaction description, frozen — repeating it under the live one would show
// the operator two answers to one question, which is the whole complaint.
export const prHasSalesNote = (task) => !!(task && task.task_type === 'payment_received');

// The card title for a pr_notification, resolved live. The stored title bakes a
// regex scrape of the description taken at insert time ("PR from KS CASH"); with
// the source row in hand there is nothing to scrape — just name the payer the
// transaction actually names. Returns null when this rule does not apply (crypto
// and blockchain cards carry GENERATED titles like "Crypto Sent: 120 USDT",
// which are not payer names and must be left alone).
export const prLiveCardTitle = (task) => {
  if (!task || task.task_type !== 'pr_notification') return null;
  if (!/^PR from /i.test(String(task.title || ''))) return null;
  const live = prRecordedName(task);
  return live ? `PR from ${live}` : null;
};

// The amount SALES stated, when finance ended up recording something different
// (bank charges deducted, customer short-paid). Returns null when they agree or
// when nothing has been recorded yet.
//
// NOTE: the authoritative amount was never stale — every task query already
// COALESCEs `amount` to the live transaction amount once finance records. What
// was missing is the OTHER side: with the live figure taking over, the number
// sales originally stated had nowhere to show, so a divergence was silent.
//
// MULTI-RECEIPT GUARD (2026-08): a payment_received task with >=2 receipt rows
// never gets `pending_tasks.transaction_id` set (Section 1 of the multi-receipt
// spec — that column only answers "which ONE bank credit IS this task?", a
// question with no answer once there is more than one). PAYMENT_RECEIVED_LIVE_
// RECORD_SQL resolves recorded_description/recorded_amount OFF that column, so
// on a multi task those flat fields — and therefore prIsRecorded() below — stay
// forever null/false even after every slip is recorded. The single-slip check
// must therefore be bypassed (not merely widened) for a multi task: it compares
// the wrong two numbers anyway (task.stated_amount is now the SUM across every
// receipt, not one slip's figure). Once every slip IS recorded the two SUMS
// (recordedTotal vs statedTotal) are directly comparable — the guard below is
// exactly the single-slip check, widened to N and re-pointed at prReceipts().
export const prStatedAmountIfDifferent = (task) => {
  if (task && task.task_type === 'payment_received') {
    const progress = prReceiptProgress(task);
    if (progress.isMulti) {
      if (!progress.allRecorded) return null; // never compare a partial sum to the whole submission
      const diff = Math.abs(progress.recordedTotal - progress.statedTotal);
      return diff < 0.005 ? null : progress.statedTotal;
    }
  }
  if (!prIsRecorded(task)) return null;
  const stated = parseFloat(task.stated_amount);
  const rec = parseFloat(task.recorded_amount);
  if (!Number.isFinite(stated) || !Number.isFinite(rec)) return null;
  if (Math.abs(rec - stated) < 0.005) return null;
  return stated;
};

// ===========================================================================
// MULTI-RECEIPT PAYMENT RECEIVED: the receipt SET behind a payment_received
// task, and the progress finance has made recording it.
//
// Operator (2026-08-03): "for normal order payment received request, sales
// should be able to upload multiple receipt … finance has to create 5 x PR
// under this one order." payment_received_receipts is the SOURCE OF TRUTH for
// that set; the server projects it, ordered seq ASC id ASC, as pr_receipts_json
// on every task query. prReceipts() is the ONE parser — the finance gallery,
// the pool/Task-Tracking galleries, the progress line and the record submit
// payload all derive from its output, so there is no second count anywhere
// that could drift from the array (the server deliberately projects no
// counts/totals of its own — see Section 0 of the spec).
// ===========================================================================

// Parse the slip set, oldest-submission-safe. A task served before this
// change (or a historical row that only ever had one receipt) carries no
// pr_receipts_json at all — synthesize a single-entry array from the task's
// own flat columns, the SAME columns PAYMENT_RECEIVED_LIVE_RECORD_SQL already
// resolves live off pt.transaction_id, so an old task renders as "1 of 1"
// through the exact same gallery code every multi task uses.
//
// `id: null` on that synthesized entry is deliberate and load-bearing: it is
// the signal every caller uses to POST *without* payment_received_receipt_id,
// which is what keeps the server on the byte-identical legacy (0/1-slip) code
// path for these tasks. Never invent an id here.
export const prReceipts = (task) => {
  if (!task || task.task_type !== 'payment_received') return [];
  const rows = parseJsonArray(task.pr_receipts_json);
  if (rows.length > 0) return rows;
  // no projected set (an old task): fall through to the single-receipt shape below
  if (!task.request_proof_url) return [];
  return [{
    id: null,
    url: task.request_proof_url,
    stated_amount: task.stated_amount ?? task.amount,
    bank_transaction_id: task.transaction_id ?? null,
    recorded_amount: task.recorded_amount,
    recorded_description: task.recorded_description,
    recorded_reference_no: task.recorded_reference_no,
    recorded_at: task.recorded_at,
    recorded_account_label: task.recorded_account_label,
  }];
};

// Counts/sums derived from prReceipts() ONLY. `bank_transaction_id == null` is
// the contract's definition of "still pending" (Section 0) — a slip is
// recorded the instant that column is stamped, independently of anything else
// on the task.
export const prReceiptProgress = (task) => {
  const receipts = prReceipts(task);
  const total = receipts.length;
  const recordedRows = receipts.filter((r) => r.bank_transaction_id != null);
  const sum = (rows, key) => rows.reduce((a, r) => a + (parseFloat(r[key]) || 0), 0);
  return {
    total,
    recorded: recordedRows.length,
    statedTotal: sum(receipts, 'stated_amount'),
    recordedTotal: sum(recordedRows, 'recorded_amount'),
    isMulti: total > 1,
    allRecorded: total > 0 && recordedRows.length === total,
  };
};

// The ONE progress line, shared by the finance modal's gallery header, the
// pending-tasks pool card and Task Tracking's detail group — so a partially
// recorded submission can never read "3 of 5" on one screen and "2 of 5" on
// another. e.g. "RM 20,000.00 of RM 50,000.00 recorded (2 of 5)".
export const prProgressText = (task) => {
  const { total, recorded, statedTotal, recordedTotal } = prReceiptProgress(task);
  return `${formatCurrency(recordedTotal)} of ${formatCurrency(statedTotal)} recorded (${recorded} of ${total})`;
};

// ===========================================================================
// PER-SLIP "WHAT FINANCE RECORDED" (operator, 2026-09-24): "for a split payment
// receipt, finance can write a different PR name on each slip (the customer
// sometimes pays through a third party), but sales can't see it from Task
// Tracking — it should show both the receipt's PR name and amount". A 2+-slip
// task never gets pt.transaction_id (no single credit IS the task), so the
// task-level recorded_description stays NULL and prRecordedName() is silent;
// the per-slip names have been on pr_receipts_json all along (resolved LIVE off
// each slip's bank transaction by every task query) — nothing rendered them.
// These are the ONE reader of those per-slip fields, shared by Task Tracking,
// the pool card and the finance record modal, so no surface can name a
// different payer for the same slip.
// ===========================================================================
// "<payer as the bank statement shows> · RM x" for a recorded slip, else null.
export const prReceiptRecordText = (r) => {
  if (!r || r.bank_transaction_id == null) return null;
  const name = String(r.recorded_description || '').trim();
  const amount = parseFloat(r.recorded_amount);
  const parts = [name || null, Number.isFinite(amount) ? formatCurrency(amount) : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'recorded';
};

// The line sales forward once finance has recorded a receipt (operator
// 2026-10-20): "PR <payer name as finance recorded it> <amount>", e.g.
// "PR ACID CLUB TRADING RM 8,000.00". ONE builder for every Copy button.
// The payer part only: the bank recorder stores "<payer> / ← <customer>" (the
// arrow half is the CRM's customer mapping, not what the statement shows).
export const prPayerNameOf = (desc) => String(desc || '').split(' / ←')[0].replace(/\s+/g, ' ').trim();
export const prCopyLine = (name, amount) => {
  const n = prPayerNameOf(name);
  const a = parseFloat(amount);
  if (!n && !Number.isFinite(a)) return null;
  return ['PR', n || null, Number.isFinite(a) ? formatCurrency(a) : null].filter(Boolean).join(' ');
};
// Per recorded slip.
export const prReceiptCopyLine = (r) =>
  (r && r.bank_transaction_id != null) ? prCopyLine(r.recorded_description, r.recorded_amount) : null;
// Task level (single-slip task: the live record's payer + the task amount).
export const prTaskCopyLine = (task) => {
  const name = prRecordedName(task);
  if (!name) return null;
  const amt = task.recorded_amount != null ? task.recorded_amount : task.amount;
  return prCopyLine(name, amt);
};

// The bank account + reference finance booked the slip on, else null.
export const prReceiptRecordSub = (r) => {
  if (!r || r.bank_transaction_id == null) return null;
  const parts = [r.recorded_account_label, r.recorded_reference_no].map((x) => String(x || '').trim()).filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

// Every recorded slip of a MULTI-receipt task, in slip order, for the task's
// "Recorded by finance" summary. Empty for a single-slip task (its live record
// is the task-level prRecordedName()) and while nothing is recorded yet.
export const prRecordedReceiptLines = (task) => {
  const receipts = prReceipts(task);
  if (receipts.length < 2) return [];
  return receipts
    .map((r, i) => ({ index: i + 1, total: receipts.length, text: prReceiptRecordText(r), sub: prReceiptRecordSub(r), copy: prReceiptCopyLine(r) }))
    .filter((x) => x.text);
};

// The claim button's label on the pool card AND Task Tracking's claimed card
// (Section 7.3) — one function so the two can never offer different wording
// for the identical next step. A partially recorded multi says how much is
// left to do; everything else (unclaimed, single-receipt, not yet started)
// keeps today's plain wording.
export const prRecordButtonLabel = (task) => {
  const { isMulti, recorded, total } = prReceiptProgress(task);
  return (isMulti && recorded > 0) ? `Record next receipt (${recorded} of ${total} done)` : 'Confirm payment received';
};

// ===========================================================================
// PAYMENT-REQUEST CLAIM GROUP — one order, one claimer (operator 2026-08-06).
//
// "All payment requests created under one order are ONE order": the server
// claims and releases a payment_request task together with its siblings (same
// order, or same order-less submission). These helpers are the ONE place the UI
// decides how to SAY that, so the pool card and the Task Tracking card can never
// describe the same group differently.
//
// Everything here reads server-projected pr_group_* fields ONLY. Nothing is
// re-derived client-side, and it could not be: the pool hides tasks claimed by
// other users, so a client counting the cards it can see would report "1 of 1"
// for a task whose sibling is in someone else's hands — precisely the state the
// badge exists to reveal. Null/absent fields (every non-payment_request task and
// every ungrouped legacy row) make all of these no-ops, which is exactly the
// pre-2026-08-06 rendering.
// ===========================================================================

export const prGroupSize = (task) => Number(task?.pr_group_total || 0);

// A group worth telling the user about: keyed, and bigger than one payment.
export const prIsGrouped = (task) => !!task?.pr_group_key && prGroupSize(task) > 1;

// "Payment 2 of 3 · claimed together" — position within the group, so finance
// can see at a glance that the card in front of them is part of a set.
export const prGroupBadgeText = (task) =>
  prIsGrouped(task) ? `Payment ${Number(task.pr_group_index)} of ${prGroupSize(task)}` : null;

// What the group IS, in words. An order-linked group really is an order; an
// order-less customer-balance submission is not, and calling it one on screen
// would name a record the operator can't go and look at. The server already
// encodes the distinction in pr_group_key ('order:12' vs 'grp:345'), so this
// reads it rather than guessing from any other field.
export const prGroupScopeLabel = (task) =>
  String(task?.pr_group_key || '').startsWith('order:') ? 'this order' : 'this request';

// How many tasks a Claim click would actually take (the group's still-pending
// members). Completed siblings are not claimed again.
export const prGroupClaimCount = (task) => Number(task?.pr_group_pending || 0);

// The claim button must state what it really does. A grouped card claims the
// whole set, and saying "Claim task" for an action that takes three payments
// would be the interface lying about the money.
export const prGroupClaimLabel = (task) => {
  // 2026-09-16: one payment per claim (like split platform buys) — the button
  // never takes siblings any more.
  return prIsGrouped(task) ? 'Claim this payment' : 'Claim task';
};

// The name of the finance user who holds this group when it is NOT the viewer.
// The server refuses such a claim (409 GROUP_CLAIMED) because part of the order
// is already someone else's, so the pool says so instead of offering a button
// that cannot work. Returns null when free, or when the viewer is the holder.
export const prGroupHeldByOther = (task, userId) => {
  // 2026-09-16: a sibling held by a peer is simply their payment — it never
  // blocks claiming this one. Kept as a no-op so the call sites read the same.
  return null;
};

// Money already prepared/paid ANYWHERE in the claim group. Release is
// all-or-nothing over the group, so every release affordance keys on THIS, not
// on the single card's paid_total — otherwise a card with no tranche of its own
// would offer a Release the server then refuses because a SIBLING has one.
export const taskGroupAllocated = (task) =>
  (task && task.pr_group_allocated != null) ? parseFloat(task.pr_group_allocated) : taskAllocated(task);

// UNCONFIRMED (prepared) money is the ONLY thing that pins a release now
// (mid-flight hand-off, operator 2026-09-02): completed tranches stay booked
// and the next claimer continues the balance. Group-aware for payment
// requests (a sibling's prepared slip pins the whole set — the server reads
// the same pr_group_prepared); a platform task reads its own detail-scoped
// tranche list. Every Release affordance keys on THIS, so no surface offers
// a release the server then refuses (and none hides one it would allow).
export const releaseBlockedByPrepared = (task) => {
  if (!task) return false;
  if (task.task_type === 'payment_request' && task.pr_group_prepared != null) {
    return Number(task.pr_group_prepared) > 0;
  }
  return taskTranches(task).some((r) => r.status === 'prepared');
};

// Does releasing THIS card hand back only one payment of a larger set? True for
// a payment-request payment and for a split platform buy's payment; the button
// label and tooltip say so, and the server releases exactly that one.
export const releaseIsPerPayment = (task) =>
  prIsGrouped(task) || (!!task && task.task_type === 'platform_order' && task.platform_order_detail_seq != null);

// The Release confirmation, ONE wording for the pool card and Task Tracking.
// Release is per task, and a split platform buy's task IS one payment (claim is
// per payment too), so the dialog names the payment and says the rest of the
// order is untouched (audit T4 / A-7 / D-5).
export const releaseConfirmText = (task) => {
  // A notice goes back to BOTH pools — sales and finance — unclaimed.
  if (task && task.task_type === 'pr_notification') {
    return 'Release this notice? It goes back to the sales and finance pools, unclaimed, and anyone can pick it up.';
  }
  const tail = 'Another finance user will be able to claim it. Completed slips stay as they are and the next claimer continues the balance; only an unconfirmed prepared slip must be cancelled or confirmed first.';
  if (prIsGrouped(task)) {
    return `Release this payment (${prGroupBadgeText(task)}) back to the pool? Only this payment is released — the other payments of ${prGroupScopeLabel(task)} are not affected. ${tail}`;
  }
  if (task && task.task_type === 'platform_order' && task.platform_order_detail_seq != null) {
    return `Release payment ${task.platform_order_detail_seq} of order #${task.platform_order_id} back to the pool? Only this payment is released — any other payment of this order you hold stays yours. ${tail}`;
  }
  return 'Release this task back to the pool? Another finance user will be able to claim it. Payments already completed stay as they are and the next claimer continues the balance; only an unconfirmed prepared slip must be cancelled or confirmed first.';
};

// ===========================================================================
// COMPLETING A PAYMENT, AND THE P2P-SELL HOLDING: one vocabulary.
//
// A P2P SELL's customer cash is booked into our bank the moment finance
// confirms it, but it is not ours yet: it sits in the bucket the Checkpoints
// page shows as "Unattributed" (NAV-neutral) until the PLATFORM ACCOUNT HANDLER
// completes the trade and releases the crypto. The confirms used to call that
// bucket "we owe unknown" (the pre-2026-10-09 name) and to say "sales completes
// the trade" — the handler does (audit D-15). These strings are the only place
// either phrase is spelled.
// ===========================================================================
export const UNATTRIBUTED_HOLDING = 'Unattributed';
const HELD_UNTIL_TRADE_DONE_TEXT =
  `held as "${UNATTRIBUTED_HOLDING}" (NAV-neutral) until the platform account handler completes the trade and releases the crypto`;

// The tooltip on the "Confirm received" / "Bank credited" / "Mark complete" /
// "Bank debited" button — Task Tracking and the Platform order modal.
export const completePaymentTooltip = (inbound = false) => (inbound
  ? `You can see this credited — confirm it now (the account is credited and it is snapshotted; the money stays held as "${UNATTRIBUTED_HOLDING}" until the platform account handler completes the trade)`
  : "The bank has debited — complete this payment now (money moves and it's snapshotted)");

// The confirm for COMPLETING one prepared payment (money moves now). Task
// Tracking and Platform Management each had their own text for this.
//   inbound  → P2P sell: "I can see the credit" (money IN)
//   flow     → 'payment_request' | 'platform_order' (what flips when the last one completes)
export function completePaymentConfirmText({
  amount,
  inbound = false,
  flow = 'payment_request',
  sourceType = null,       // 'customer' → a customer fronts the payment
  payeeName = null,        // payment_request: whose balance updates
} = {}) {
  const rm = formatCurrency(amount);
  if (inbound) {
    return `Confirm ${rm} has been RECEIVED?\n\n`
      + 'Only do this once you can see the credit in the account.\n'
      + '• the bank/cash account is credited now\n'
      + `• the money is ${HELD_UNTIL_TRADE_DONE_TEXT}\n`
      + '• sales is notified: "PR (payment received)"\n'
      + '• the platform account handler is notified once the full amount is in\n'
      + '• it is recorded on the checkpoint timeline';
  }
  const where = String(sourceType) === 'customer'
    ? 'the fronting customer will now be owed this amount'
    : 'the bank/cash account will be debited now';
  const subject = flow === 'platform_order'
    ? 'the order flips to PAID once all its payments are completed'
    : `${payeeName || 'the customer'}'s balance updates`;
  return `Mark this ${rm} payment as completed?\n\n`
    + 'Only do this once the bank has actually taken the money.\n'
    + `• ${where}\n`
    + `• ${subject}\n`
    + '• it is recorded on the checkpoint timeline';
}

// ADMINISTRATOR COMPLETING ON FINANCE'S BEHALF (operator 2026-10-30). The
// finance user who prepared a slip may be unavailable; an administrator can then
// press Complete on a PREPARED slip. ONE confirm, ONE visibility rule and ONE
// hint for all three flows (payouts, platform payments, expense slips); the
// server enforces the same (lib/slip-completion).
export const ADMIN_CAN_COMPLETE_HINT = '(an administrator can do it for them)';
export const completeOnBehalfConfirmText = ({ preparerName = null, adminName = null } = {}) =>
  `Complete this payment on behalf of ${preparerName || 'finance'}? It will be recorded as completed by you (${adminName || 'you'}). Only do this if the bank has really debited.`;
// The administrator sees Complete on a prepared slip they are not already
// entitled to complete as its holder. `holderId` is the finance user holding it.
export const canAdminCompleteOnBehalf = (user, slip, holderId = null) => (
  !!user && user.role === 'administrator' && !!slip && slip.status === 'prepared'
  && !(holderId != null && holderId === user.id)
);

// ===========================================================================
// UNDOING A PAYMENT: ONE confirm text, three surfaces.
//
// "Revert / cancel this payment" is offered from three different places and each
// had hand-written its own dialog, so the same destructive action was described
// three different ways — one of them omitting what actually happens to the money:
//   • TaskTracking.handleRevertPayment          (payment_request + platform)
//   • FulfillPaymentRequestModal.onCancelPayment (inside the fulfil modal)
//   • TransactionHistory (the grouped payout row in the customer ledger)
// The MECHANICS stay per-caller (each has its own endpoint, refresh and error
// handling); only the wording is shared, and it converges on the most complete
// version — nobody loses a warning they used to get.
//
// Two states, and they are not the same act:
//   prepared  → nothing has moved yet; this only throws the intent away.
//   completed → money and ledgers reverse and the reversal is snapshotted.
//             EXCEPT a RECEIVED payment on a P2P sell (inbound): that revert is
//             a RELEASE (operator 2026-10-11) — the credit stays in the bank, it
//             is NOT debited, it goes back to Unattributed and the task returns
//             to the finance pool. The refund is a separate payout.
// ===========================================================================
// The tooltip on the Revert / × button of a COMPLETED received payment — the
// same promise as the confirm below, in one line.
export const REVERT_RECEIVED_TOOLTIP =
  `Revert this received payment: nothing is debited — the credit stays in the bank and goes back to "${UNATTRIBUTED_HOLDING}", and the task returns to the finance pool`;
// The hint above the "Payments (completed)" list on a paid P2P sell.
export const REVERT_RECEIVED_HINT =
  `Recorded the wrong credit? Reverting a received payment does NOT debit the bank: the credit stays in the account and goes back to "${UNATTRIBUTED_HOLDING}", this order reopens, and the task returns to the finance pool. A refund to the customer is a separate payout.`;

export function revertPaymentConfirmText({
  amount,                 // the figure being undone
  status,                 // 'prepared' → cancel; anything else → revert
  flow = 'payment_request', // 'payment_request' | 'platform_order'
  inbound = false,        // platform P2P sell: the money came IN to us
  linked = false,         // inbound only: a credit CLAIMED from an existing PR (bank_tx_linked_at)
  sourceType = null,      // 'customer' → a customer fronted this payment
  payeeName = null,       // who becomes owed again when a request reopens
  funderName = null,      // who stops being owed (customer front)
  slipCount = 1,          // grouped row: how many tranches go down together
} = {}) {
  const rm = formatCurrency(amount);

  if (String(status) === 'prepared') {
    // Money-IN: there is no bank instruction to un-send, only a claim that the
    // credit arrived — so the warning is about the money, not the transfer.
    if (inbound) {
      return `⚠️ Cancel this recorded credit of ${rm}?\n\n`
        + 'Only do this if the money has NOT arrived. No money has moved yet; '
        + 'the proof is removed and the amount is freed to record again.';
    }
    return `⚠️ Cancel this prepared payment of ${rm}?\n\n`
      + 'Only do this if the bank has NOT yet verified/authorised the transfer. '
      + 'If the bank has already processed it, do NOT cancel — mark it complete instead.\n\n'
      + 'No money has moved yet; the receipt is removed and the amount is freed to prepare again.';
  }

  if (inbound && linked) {
    // A credit finance had already recorded and sales CLAIMED for the order:
    // undoing the claim moves nothing and finance's own record is untouched.
    return `Unlink this payment of ${rm}?\n\n`
      + `• nothing is debited: the credit stays in the bank/cash account as finance recorded it and goes back to "${UNATTRIBUTED_HOLDING}"\n`
      + '• it can be claimed again by any order\n'
      + '• a PAID order reopens\n'
      + '• it is recorded on the snapshot timeline (NAV-neutral)';
  }
  if (inbound) {
    return `Revert this RECEIVED payment of ${rm}?\n\n`
      + `• nothing is debited: the credit stays in the bank/cash account and goes back to "${UNATTRIBUTED_HOLDING}" (NAV-neutral)\n`
      + '• the task is released back to the finance pool — any finance user can pick it up\n'
      + '• a PAID order reopens\n'
      + '• this is NOT a refund: to pay the customer back, assign the credit to them and raise a separate payout (Request Payment with a remark)\n'
      + '• if the money never actually arrived, delete the credit yourself afterwards (Transaction History)\n'
      + '• it is recorded on the snapshot timeline';
  }

  const bullets = [
    String(sourceType) === 'customer'
      ? `${funderName || 'the fronting customer'} is un-owed`
      : 'the bank/cash account is refunded',
  ];
  if (flow === 'platform_order') {
    bullets.push('a PAID order reopens for payment');
  } else {
    bullets.push(`${payeeName || 'the customer'} is owed this amount again`);
    bullets.push('the request reopens for payment');
  }
  bullets.push('the reversal is recorded on the snapshot timeline');

  const n = Number(slipCount) || 1;
  return `Revert this payment${n > 1 ? ` (${n} slips)` : ''} of ${rm}?\n\n`
    + bullets.map((b) => `• ${b}`).join('\n');
}

// ===========================================================================
// PLATFORM ORDER PAYMENTS: what the BANK ROW says vs what was typed on the slip.
//
// A platform_order_payments row carries two different texts and they must never
// be confused, for exactly the same reason as the PR task above:
//   * pop.description — the memo finance typed when RECORDING, or, on a credit
//     claimed from an already-recorded PR, a canned "Linked to payment received
//     … already recorded by finance" note. A write-time copy either way.
//   * pop.recorded_description — the description of the bank transaction the row
//     actually points at (pop.bank_transaction_id), resolved LIVE by the server.
//     That is the text that mirrors the bank statement, and the text finance
//     corrects when the payer turns out to be a different name.
//
// Once the bank row exists, THAT is authoritative. These two helpers are the
// only place that decides so, shared by the finance fulfil modal and both of
// PlatformManagement's payment blocks, so no surface can name a different payer
// for the same ringgit.
// ===========================================================================
// MONEY-IN ONLY. On a sell the bank row's description IS the payer as the
// statement shows them — the fact that goes stale. On a payout the same field is
// a generated stamp ("Platform order #12 payment") that names nobody, so
// surfacing it would add a line of noise to every payout slip and teach the
// operator to ignore this line. Direction comes from the row (pop.direction),
// never from the screen it is being rendered on.
export const popRecordedName = (pop) => {
  if (String(pop?.direction) !== 'in') return null;
  const live = String(pop?.recorded_description || '').trim();
  return live || null;
};

// The slip's own memo, but ONLY when it adds something the live line does not.
// Suppressed when identical (nothing gained) and when the live record exists and
// the memo is the canned link note (it says nothing a claimed row does not
// already show through its "claimed from an existing PR" chip).
export const popSlipNote = (pop) => {
  const memo = String(pop?.description || '').trim();
  if (!memo) return null;
  const live = popRecordedName(pop);
  if (!live) return memo;
  if (memo === live) return null;
  if (pop?.bank_tx_linked_at && /^Linked to payment received /i.test(memo)) return null;
  return memo;
};

// "HTX HBL · otc" — the platform account and wallet a platform-order task's
// order sits on (operator 2026-10-09: "at pending task for platform order,
// it should also show what platform is it somewhere else"). ONE formatter for
// the pool card and the Task Tracking card; reads the three parts the task
// endpoints project off the order (platform_platform_name /
// platform_account_name / platform_wallet_type). Null when the task is not a
// platform task or the order has no wallet (legacy).
export function formatPlatformAccountLabel(task) {
  if (!task) return null;
  const platform = task.platform_platform_name ? String(task.platform_platform_name).toUpperCase() : '';
  const account = task.platform_account_name ? String(task.platform_account_name) : '';
  const wallet = task.platform_wallet_type ? String(task.platform_wallet_type) : '';
  const head = [platform, account].filter(Boolean).join(' ');
  if (!head && !wallet) return null;
  return wallet ? `${head || 'Platform'} · ${wallet}` : head;
}

// ===========================================================================
// EXPENSE PAYMENT SLIPS — "Finance's receipt" on an expense_payment task
// (audit T27, 2026-10-10). The slips (expense_detail_payments) are the SOURCE
// OF TRUTH for a payee's receipts; the server projects them, oldest first, as
// expense_detail_slips_json (same shape as payment_receipts_json, plus
// prepared_* because a prepared slip has no paid_by/paid_at yet). The detail
// row's own payment_proof_url is a dead legacy copy and is never read. These
// are the ONE parser/formatters, shared by Task Tracking and the pool card so
// the two can never show a different slip set or wording for the same payee.
// ===========================================================================

// The payee's slip set, oldest first. [] for any other task type. A payload
// from before the JSON existed (a stale backend during a deploy window) falls
// back to the single expense_detail_receipt_url, so one image still shows.
export const expenseSlips = (task) => {
  if (!task || task.task_type !== 'expense_payment') return [];
  try {
    const parsed = task.expense_detail_slips_json ? JSON.parse(task.expense_detail_slips_json) : null;
    if (Array.isArray(parsed)) return parsed.filter((s) => s && typeof s === 'object');
  } catch (e) { /* fall through to the single-url shape below */ }
  if (!task.expense_detail_receipt_url) return [];
  return [{
    id: null,
    url: task.expense_detail_receipt_url,
    amount: null,
    status: task.expense_detail_status === 'completed' ? 'completed' : 'prepared',
  }];
};

// ---- THE EXPENSE-PAYEE GATE (audit 2026-10 T18 / D-4) ----
// Frontend twin of the server's expensePayeeGate (backend/server.js, the
// authority for POST /api/tasks/:id/claim and /release on an expense_payment
// task): SAME inputs, SAME answers. The pool card, Task Tracking and the
// Expenses & Profit page all decide Claim / Release through it. Before, the
// pool hid Release once ANY slip existed while the server only refuses an
// UNCONFIRMED (prepared) one, and Expenses offered Claim only on a 'pending'
// payee while the server and the pool allowed any unclaimed one.
//   open           the task is pending, the claim is approved and this payee
//                  is not fully paid
//   canClaim       a finance user, open, nobody holds it
//   canRelease     I hold it, open, no prepared slip (completed slips never
//                  pin a release: the next claimer pays the remainder)
//   releaseBlocked I hold it, open, but a prepared slip pins it (say why)
export const EXPENSE_RELEASE_BLOCKED_TEXT = 'A prepared (unconfirmed) slip is on this payee — cancel it, or mark it complete once the bank has debited, before releasing.';
export const expensePayeeGate = ({ taskStatus, expenseStatus, detailStatus, claimedBy, preparedCount }, user) => {
  const me = user && user.id != null ? Number(user.id) : null;
  const open = taskStatus === 'pending' && expenseStatus === 'approved' && detailStatus !== 'completed';
  const held = claimedBy != null;
  const mine = held && me != null && Number(claimedBy) === me;
  const prepared = Number(preparedCount || 0) > 0;
  return {
    open,
    held,
    mine,
    canClaim: !!user && user.role === 'finance' && open && !held,
    canRelease: open && mine && !prepared,
    releaseBlocked: open && mine && prepared,
  };
};
// The gate's inputs from an expense_payment TASK (pool / Task Tracking) ...
export const expenseGateInputFromTask = (task) => ({
  taskStatus: task?.status,
  expenseStatus: task?.expense_status,
  detailStatus: task?.expense_detail_status,
  claimedBy: task?.claimed_by,
  preparedCount: task?.expense_detail_prepared,
});
// ... and from a payee row on the Expenses page (GET /api/expenses details).
export const expenseGateInputFromDetail = (detail, expense) => ({
  taskStatus: detail?.task_status,
  expenseStatus: expense?.status,
  detailStatus: detail?.status,
  claimedBy: detail?.task_claimed_by,
  preparedCount: (detail?.payments || []).filter((s) => s && s.status === 'prepared').length,
});

// "Finance's receipt #2 of 3 — RM 120.00" (the "#n of N" only when 2+ slips;
// the amount only when known).
export const expenseSlipLabel = (slip, index, total) => {
  const amount = slip && slip.amount != null && slip.amount !== '' ? ` — ${formatCurrency(slip.amount)}` : '';
  return `Finance's receipt${total > 1 ? ` #${index + 1} of ${total}` : ''}${amount}`;
};

// ---- SLIP vocabulary, shared by EVERY slip kind (payout tranche, platform
// payment, expense payee slip) and rendered by components/PaymentSlip.js. ----

// "bank/cash" or "customer account" — where the slip's money came from ('' if unknown).
export const slipSourceText = (slip) => {
  if (!slip || !slip.source_type) return '';
  return slip.source_type === 'customer' ? 'customer account' : 'bank/cash';
};

// The status chip: a prepared slip has moved no money yet; a completed one has.
// `inbound` (a P2P sell) words a prepared row as a recorded credit.
export const slipChip = (slip, inbound = false) => (
  slip && slip.status === 'prepared'
    ? { text: inbound ? 'RECORDED · awaiting credit' : 'PREPARED · awaiting bank', prepared: true }
    : { text: 'COMPLETED', prepared: false }
);

// WHO handled the slip and WHEN, by reference to users: completed -> "Paid by
// <paid_by> on <paid_at>", prepared -> "Prepared by <prepared_by> on
// <prepared_at>". A payout / platform row only carries its uploader (paid_by),
// so that is the prepared-state fallback. Null when there is neither.
//
// ON BEHALF (2026-10-30): when an ADMINISTRATOR completed a slip another person
// prepared, the line reads "Completed by <admin> (on behalf of <preparer>)" and
// `preparedBy` carries the preparer for a second "Prepared by" line - the
// record shows who pressed Complete AND who prepared it. The completer comes
// from completed_by_name (platform slips) else paid_by_name (payouts / expenses
// write the completer there); the preparer from prepared_by_name.
export const slipCompleterName = (slip) => (slip ? (slip.completed_by_name || slip.paid_by_name || slip.uploaded_by_name || slip.paid_by_username || null) : null);
export const slipWho = (slip) => {
  if (!slip) return null;
  const prepared = slip.status === 'prepared';
  const completer = slipCompleterName(slip);
  const name = prepared ? (slip.prepared_by_name || slip.paid_by_name) : completer;
  const at = prepared ? (slip.prepared_at || slip.paid_at) : (slip.completed_at || slip.paid_at);
  if (!name && !at) return null;
  if (!prepared && slip.completed_by_role === 'administrator' && slip.prepared_by_name && completer && slip.prepared_by_name !== completer) {
    return { verb: 'Completed by', name: `${completer} (on behalf of ${slip.prepared_by_name})`, at: at || null, preparedBy: slip.prepared_by_name };
  }
  return { verb: prepared ? 'Prepared by' : 'Paid by', name: name || 'finance', at: at || null };
};

// WHO PAID — names come ONLY from the slip rows (audit 2026-10 T18 / A-12):
// the distinct people on the COMPLETED slips, in slip order. Never the claimer
// (a mid-flight hand-off makes them different people) and never a task-side
// stamp (pt.paid_by named whoever last prepared, completed or reverted).
// Accepts every slip shape in the app: task-list slips (paid_by_name), the
// order modal's /payments rows (uploaded_by_name) and the Expenses page's
// payee slips (paid_by_username).
export const slipPayerNames = (slips) => {
  const out = [];
  (slips || []).forEach((s) => {
    if (!s || s.status !== 'completed') return;
    const n = slipCompleterName(s);
    if (n && !out.includes(n)) out.push(n);
  });
  return out;
};
export const slipPayerText = (slips) => slipPayerNames(slips).join(', ') || null;

// The expense names the pool card imports — the same functions.
export const expenseSlipSourceText = slipSourceText;
export const expenseSlipChip = (slip) => slipChip(slip, false);
export const expenseSlipWho = slipWho;

// ===========================================================================
// TASK TYPE VOCABULARY (audit D-16). Task Tracking's badge showed the raw
// "PAYMENT_REQUEST" while the pool called the same task a "Payout". ONE map,
// worded like the pool's categories (singular), so a task carries one name.
// ===========================================================================
export const TASK_TYPE_META = {
  payment_request: { label: 'Payout', color: '#0969da' },
  payment_received: { label: 'Payment received', color: '#1a7f37' },
  platform_order: { label: 'Platform order', color: '#3b82f6' },
  platform_order_approval: { label: 'Order approval', color: '#b45309' },
  expense_payment: { label: 'Expense payment', color: '#9a6700' },
  expense_approval: { label: 'Expense approval', color: '#5b21b6' },
  expense_claim: { label: 'Expense claim', color: '#9a6700' },
  pr_notification: { label: 'Notice', color: '#1a7f37' },
};
export const taskTypeMeta = (task) => {
  const type = task?.task_type;
  if (type === 'pr_notification') {
    // Same split as the pool's "Crypto notices" / "Bank notices" pills.
    const crypto = !!(task.blockchain_transaction_id || task.crypto_transaction_id);
    return { label: crypto ? 'Crypto notice' : 'Bank notice', color: '#1a7f37' };
  }
  const meta = TASK_TYPE_META[type];
  if (meta) return meta;
  const words = String(type || 'task').replace(/_/g, ' ');
  return { label: words.charAt(0).toUpperCase() + words.slice(1), color: '#57606a' };
};

// ---- ONE parser per projected json (audit D-17: the tranche parse lived in
// four places). Always an array of objects; a malformed / masked (null) field
// is []. ----
export const parseJsonArray = (raw) => {
  if (Array.isArray(raw)) return raw.filter((x) => x && typeof x === 'object');
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => x && typeof x === 'object') : [];
  } catch (e) { return []; }
};

// The task's OWN money rows (payment_request_payments / this platform task's
// platform_order_payments; order-wide on a BUY approval card).
export const taskTranches = (task) => parseJsonArray(task?.payment_receipts_json);

// Every finance task of a platform task's order (platform_finance_tasks_json):
// [{task_id, seq, status, claimed_by, claimed_by_username, claimed_at, amount,
//   prepared_total, completed_total, completed_at}]. Non-sensitive.
export const platformFinanceTasks = (task) => parseJsonArray(task?.platform_finance_tasks_json);

// Finance's slips for the WHOLE order (platform_payments_json). For a viewer
// outside the task's payee-viewer set (taskPayeeAccess below) the server
// REDACTS each slip (image + bank text nulled, locked:1) but keeps its amount /
// status / who / when, so the stage bar and the per-payment chips still read.
export const platformOrderSlips = (task) => parseJsonArray(task?.platform_payments_json);

// ---- WHO MAY READ A MONEY-OUT TASK'S PAYEE DETAILS (audit T3) ----
// Client mirror of the server's maskTaskListForViewer (backend/server.js). The
// SERVER is the authority — it nulls what a viewer may not see on every task
// list and sets payee_details_locked; this only lets a card decide what to
// draw with the SAME rule, so the two can never disagree. Returns
//   'full' — administrator, the task's creator / submitter, a platform
//            account's live handler, or the holder of a non-split task;
//   'own'  — the holder of ONE payment of a split buy (its own payee only);
//   'none' — everyone else.
export const taskPayeeAccess = (task, user) => {
  if (!task || !user) return 'none';
  // An auditor reads as an administrator (utils/viewRole — the server's twin).
  if (viewRole(user) === 'administrator') return 'full';
  const me = user.id;
  if (me == null) return 'none';
  if (task.created_by === me) return 'full';
  const platform = task.task_type === 'platform_order' || task.task_type === 'platform_order_approval';
  if (platform && (task.platform_submitted_by === me || task.platform_handler_id === me)) return 'full';
  if (task.claimed_by === me) {
    return (task.task_type === 'platform_order' && task.platform_order_detail_seq != null) ? 'own' : 'full';
  }
  return 'none';
};

// ---- A task's AMOUNT and DIRECTION, from the LIVE source row ----
// Every task list projects the linked transaction's token
// (blockchain_token_symbol / crypto_tx_crypto_type) and direction
// (blockchain_direction / crypto_tx_direction / bank_tx_direction). Both task
// screens used to scrape these out of the stored title with a regex
// ("Crypto Sent: 1.50 USDT"), which only worked where the title happened to
// say it — and a bank payer named "SENTUL…" would have read as an outflow.
// A crypto pr_notification's amount is in the TOKEN, never MYR.
export const taskAmountIsCrypto = (task) => !!(task?.blockchain_transaction_id || task?.crypto_transaction_id);
export const taskAmountToken = (task) => {
  const t = task?.blockchain_token_symbol || task?.crypto_tx_crypto_type;
  return t ? String(t).toUpperCase() : null;
};
// THE task-card money formatter (audit 2026-10 D-11) — ONE copy for the pool
// card and Task Tracking. The unit comes from the LIVE fields, never the title:
//   * a crypto notice → its token (blockchain_token_symbol / crypto_tx_crypto_type)
//   * an expense task → the claim's currency (expense_currency)
//   * a platform task → the order's fiat currency (platform_fiat_currency)
//   * everything else → MYR
// Precision follows the unit (maxInputDecimals, utils/helpers): fiat and
// stablecoins 2 dp; any other coin up to 8 dp with trailing zeros trimmed (but
// never fewer than 2) — 0.00000282 BTC used to read "0.00 BTC".
export const taskAmountUnit = (task) => {
  if (taskAmountIsCrypto(task)) return taskAmountToken(task);
  if (task?.task_type === 'expense_payment' || task?.task_type === 'expense_approval') {
    return task.expense_currency ? String(task.expense_currency).toUpperCase() : 'MYR';
  }
  if ((task?.task_type === 'platform_order' || task?.task_type === 'platform_order_approval') && task.platform_fiat_currency) {
    return String(task.platform_fiat_currency).toUpperCase();
  }
  return 'MYR';
};
// Format ANY figure of a task (its amount, what is left of it …) in the task's
// own unit. `unit` null = a crypto notice whose token is unknown: bare number.
export const formatTaskMoney = (amount, task) => {
  const unit = taskAmountUnit(task);
  const n = parseFloat(amount || 0);
  const value = Number.isFinite(n) ? n : 0;
  const maxDp = unit ? maxInputDecimals(unit) : 8;
  if (unit && /^[A-Z]{3}$/.test(unit) && maxDp === 2) {
    // A 3-letter fiat code: the locale currency format ("RM 1,200.00").
    try {
      return new Intl.NumberFormat('en-MY', { style: 'currency', currency: unit }).format(value);
    } catch (e) { /* not an ISO code (e.g. a 3-letter coin) — fall through */ }
  }
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(2, maxDp),
  }).format(value);
  return unit ? `${formatted} ${unit}` : formatted;
};
export const formatTaskAmount = (task) => formatTaskMoney(task?.amount, task);
// The (id, source) of the transaction a pr_notification task points at — bank
// money tx, manual crypto tx or auto-fetched blockchain tx, whichever FK the
// task carries. null when it references none. Shared by the pool and Task
// Tracking (audit D-17: two copies).
export const resolveTaskTx = (task) => {
  if (task?.blockchain_transaction_id) return { id: task.blockchain_transaction_id, source: 'blockchain' };
  if (task?.crypto_transaction_id) return { id: task.crypto_transaction_id, source: 'crypto' };
  if (task?.transaction_id) return { id: task.transaction_id, source: 'money' };
  return null;
};

// The title a card shows. The stored title bakes in the customer name from
// creation time and goes stale if the customer is later renamed;
// task.customer_name is resolved live via JOIN, so prefer it — rewrite only
// the "<prefix> - <name>" tail, keeping the prefix. A "PR from <payer>" card
// names the PAYER, read off the referenced transaction (prLiveCardTitle), never
// the insert-time regex scrape. Falls back to the stored title.
export const displayTaskTitle = (task) => {
  const live = prLiveCardTitle(task);
  if (live) return live;
  if (task.customer_name && task.title && task.title.includes(' - ')) {
    return `${task.title.split(' - ')[0]} - ${task.customer_name}`;
  }
  return task.title;
};

// Inflow (received) vs outflow (sent) — decides which classify actions a
// pr_notification offers (Profit/Deposit vs Expense/Withdraw).
const INFLOW_DIRECTIONS = new Set(['incoming', 'credit', 'swap_to', 'convert_in']);
export const taskIsInflow = (task) => {
  const dir = task?.blockchain_direction || task?.crypto_tx_direction || task?.bank_tx_direction;
  return dir ? INFLOW_DIRECTIONS.has(dir) : true;
};

// The SELL customer-receipt SET (platform_order_receipts, projected as
// platform_receipt_proof_urls_json — "1 receipt = 1 PR"). The set is the only
// source: the old single column was its first entry and no order has a receipt
// without a set (0 on both tenants, audit D-19). [] on a buy.
export const platformSellReceipts = (task) =>
  parseJsonArray(task?.platform_receipt_proof_urls_json).filter((r) => r.url);

// WHICH slips belong to payee #seq. A split buy labels each row with its
// detail_seq; a single-payee order's rows carry detail_seq NULL. Same rule the
// order modal used inline (tranchesFor) — now one definition for both surfaces.
export const slipsForPayee = (slips, seq, payeeCount) => (slips || []).filter((p) =>
  (Number(payeeCount) <= 1)
    ? (p.detail_seq == null || Number(p.detail_seq) === Number(seq))
    : Number(p.detail_seq) === Number(seq));

// Once a payee is PREPARED its account details have done their job for
// finance — hiding them stops an accidental second transfer (operator
// 2026-08-28). Sales / handler / admin keep seeing them. One rule for the order
// modal and the task card.
export const platformPayeeDetailsHidden = (viewerRole, payeeSlips) =>
  viewerRole === 'finance' && (payeeSlips || []).length > 0;

// ===========================================================================
// THE STAGE MODEL — ONE definition of "where is this task, who did each step,
// and what happens next" (operator 2026-10-10: "a completion bar about a
// particular task, so we know what we have done / what haven't been done and
// should be done in future"; audit T1 / A-4 / D-1: the same paid order read
// "Claimed" in Task Tracking, "mark complete" on the pool card and "paid" on
// Platform Orders).
//
// taskSteps(task) → ordered [{ key, label, state, who, at, hint, detail }]
//   state: 'done' | 'current' | 'todo' | 'cancelled'
//   who/at: from the record (usernames by reference, UTC timestamps — render
//           with utils/helpers formatDate, never by hand)
//   hint:   on the CURRENT step (and a cancelled one): what happens next, in
//           plain words
//   detail: a short progress note ("RM 1,000.00 of RM 2,129.40 prepared",
//           "1 of 2 payments paid")
// Every fact is a field the task lists project by reference off the business
// record (the order, its payments and finance tasks, the slip rows, users) —
// nothing here is stored, so the bar can never disagree with the order. Each
// step's state is decided from its OWN fact, so an out-of-order record (money
// recorded before the approval, say) shows as exactly that instead of being
// smoothed over. The FIRST step that is not done is the current one.
//
// taskStepSummary(task) is the compact form ("Step 5 of 6 · Order paid" + the
// next step's hint) used by the collapsed Task Tracking row and the pool pill.
// ===========================================================================
const EPS = 0.01;
const toNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const isCovered = (part, whole) => toNum(whole) > 0 && toNum(part) >= toNum(whole) - EPS;
const latestBy = (rows, key) => rows.reduce((best, r) => (r && r[key] && (!best || String(r[key]) > String(best[key])) ? r : best), null);
const mkStep = (key, label, done, extra = {}) => ({
  key, label, state: done ? 'done' : 'todo', who: null, at: null, hint: null, detail: null, ...extra,
});

// Mark the first open step current; on a terminal (cancelled/rejected/…) record
// keep what was done and end on one red step — the remaining steps will never
// happen, so they are not drawn.
const finalizeSteps = (steps, terminal) => {
  const clean = steps.map((s) => ({ ...s, who: s.who || null, at: s.at || null }));
  if (terminal) {
    return [...clean.filter((s) => s.state === 'done').map((s) => ({ ...s, hint: null })),
      { key: terminal.key || 'cancelled', label: terminal.label, state: 'cancelled', who: terminal.who || null, at: terminal.at || null, hint: terminal.hint || null, detail: null }];
  }
  let currentSet = false;
  return clean.map((s) => {
    if (s.state === 'done') return { ...s, hint: null };
    if (!currentSet) { currentSet = true; return { ...s, state: 'current' }; }
    return { ...s, hint: null };
  });
};

const taskCancelledTerminal = (task) => (task?.status === 'dismissed' ? {
  key: 'cancelled',
  label: task.cancelled_by_username ? 'Cancelled' : 'Dismissed',
  who: task.cancelled_by_username || null,
  at: task.cancelled_at || task.completed_at || null,
  hint: task.cancel_reason ? `Reason: ${task.cancel_reason}` : null,
} : null);

// ---- PLATFORM (platform_order + platform_order_approval) ----
const platformOrderStatusOf = (task) => {
  if (task.platform_order_status) return task.platform_order_status;
  // A payload from before platform_order_status existed (deploy window):
  // the server-derived stage carries the same order states.
  const ws = task.workflow_stage;
  if (['completed', 'paid', 'cancelled', 'abandoned'].includes(ws)) return ws;
  return 'pending';
};

const platformSteps = (task, scope) => {
  const isSell = isPlatformMoneyIn(task);
  const orderStatus = platformOrderStatusOf(task);
  const approval = task.platform_approval_status || null;
  const onBehalf = Number(task.platform_on_behalf) === 1 || !!approval || task.task_type === 'platform_order_approval';
  const handler = task.platform_handler_username || (task.task_type === 'platform_order_approval' ? task.claimed_by_username : null);
  const orderPaid = orderStatus === 'paid' || orderStatus === 'completed';
  const financeTasks = platformFinanceTasks(task).filter((f) => f.status !== 'dismissed');
  const isSplit = financeTasks.some((f) => f.seq != null) || task.platform_order_detail_seq != null;
  const payCount = financeTasks.length;
  const fmt = formatCurrency;

  // Money in scope: THIS payment task, or the whole order (approval card).
  const orderScope = scope === 'order';
  const amount = orderScope ? toNum(task.platform_fiat_amount ?? task.amount) : toNum(task.amount);
  const prepared = orderScope ? toNum(task.platform_order_paid_total) : toNum(task.paid_total);
  const completed = orderScope ? toNum(task.platform_order_completed_total) : toNum(task.completed_total);
  const ownSlips = taskTranches(task);
  const orderSlips = platformOrderSlips(task);
  const slips = orderScope ? (orderSlips.length ? orderSlips : ownSlips) : ownSlips;
  const lastSlip = latestBy(slips, 'paid_at');
  const taskDone = task.task_type === 'platform_order' && task.status === 'completed';

  const steps = [];
  steps.push(mkStep('submitted', 'Submitted', true, {
    who: task.platform_submitted_by_username || task.created_by_username,
    at: task.platform_submitted_at || task.created_at,
  }));

  if (onBehalf) {
    if (approval === 'pending_approval') {
      steps.push(mkStep('approval', 'Approved by handler', false, {
        hint: `Waiting for ${handler || 'the account handler'} to approve or reject the submission`,
      }));
    } else {
      // approved (or later revoked — a revoke overwrites approval_decided_by
      // with the revoker, so the approver is not named then).
      steps.push(mkStep('approval', 'Approved by handler', true, approval === 'approved' ? {
        who: task.platform_approval_decided_by_username, at: task.platform_approval_decided_at,
      } : {}));
    }
  }

  // Finance's claim: this task's holder, or (order scope) every payment task's.
  if (orderScope) {
    const claimed = financeTasks.filter((f) => f.claimed_by != null || f.status === 'completed');
    const names = [...new Set(claimed.map((f) => f.claimed_by_username).filter(Boolean))];
    steps.push(mkStep('claimed', 'Claimed by finance', payCount > 0 && claimed.length === payCount, {
      who: names.join(', ') || null,
      at: payCount === 1 ? financeTasks[0].claimed_at : null,
      detail: payCount > 1 ? `${claimed.length} of ${payCount} payments claimed` : null,
      hint: payCount === 0 ? 'Finance is asked once the submission is approved' : 'Waiting for a finance user to claim it',
    }));
  } else {
    steps.push(mkStep('claimed', 'Claimed by finance', !!task.claimed_by || taskDone, {
      who: task.claimed_by_username, at: task.claimed_at,
      hint: 'Waiting for a finance user to claim it',
    }));
  }

  if (isSell) {
    const confirmed = isCovered(completed, amount) || orderPaid;
    steps.push(mkStep('payment', 'Customer payment confirmed', confirmed, {
      who: lastSlip?.paid_by_name, at: lastSlip?.paid_at,
      detail: !confirmed && prepared > EPS ? `${fmt(prepared)} of ${fmt(amount)} recorded · ${fmt(completed)} confirmed` : null,
      hint: prepared - completed > EPS
        ? 'Finance confirms the credit once it shows in the account'
        : 'Finance records the customer’s payment against our account',
    }));
  } else {
    const preparedDone = isCovered(prepared, amount) || orderPaid || taskDone;
    let preparedDetail = null;
    if (orderScope && payCount > 1) {
      const n = financeTasks.filter((f) => isCovered(f.prepared_total, f.amount) || f.status === 'completed').length;
      preparedDetail = `${n} of ${payCount} payments prepared`;
    } else if (!preparedDone && prepared > EPS) {
      preparedDetail = `${fmt(prepared)} of ${fmt(amount)} prepared`;
    } else if (slips.length > 1) {
      preparedDetail = `${slips.length} slips`;
    }
    steps.push(mkStep('prepared', 'Payment prepared', preparedDone, {
      who: lastSlip?.prepared_by_name || lastSlip?.paid_by_name, at: lastSlip?.paid_at, detail: preparedDetail,
      hint: 'Finance prepares the payment: uploads the slip (no money moves yet)',
    }));

    const completeDone = isCovered(completed, amount) || orderPaid || taskDone;
    let completeDetail = null;
    if (orderScope && payCount > 1) {
      const n = financeTasks.filter((f) => isCovered(f.completed_total, f.amount) || f.status === 'completed').length;
      completeDetail = `${n} of ${payCount} payments completed`;
    } else if (!completeDone && completed > EPS) {
      completeDetail = `${fmt(completed)} of ${fmt(amount)} completed`;
    }
    // WHO = the payers on the completed slips in scope (slipPayerText — the
    // same names as the "settled by" chip; audit 2026-10 T18), never a
    // task/order-side stamp. WHEN: a single-payment order flips to paid in
    // the SAME action as its last completion (the order's paid_at); a split
    // payment's task completes the moment its own payment is covered.
    const completeWhoAt = {
      who: slipPayerText(slips),
      at: (!isSplit || orderScope) ? task.platform_paid_at : task.completed_at,
    };
    steps.push(mkStep('completed', 'Marked complete (bank debited)', completeDone, {
      ...(completeDone ? completeWhoAt : {}), detail: completeDetail,
      hint: `Finance marks the bank debit complete once the bank has taken the money ${ADMIN_CAN_COMPLETE_HINT}`,
    }));
  }

  const paidCount = financeTasks.filter((f) => isCovered(f.completed_total, f.amount) || f.status === 'completed').length;
  steps.push(mkStep('order_paid', 'Order paid', orderPaid, {
    // Settled by every payer on the order (order-wide slips when projected).
    who: slipPayerText(orderSlips.length ? orderSlips : ownSlips), at: task.platform_paid_at,
    detail: isSplit && payCount > 1 && !orderPaid ? `${paidCount} of ${payCount} payments paid` : null,
    hint: isSplit && payCount > 1
      ? `Waiting for the other payment${payCount - paidCount > 1 ? 's' : ''} of this order — ${paidCount} of ${payCount} paid`
      : (isSell ? 'Paid once the customer’s payment is confirmed' : 'Paid once every payment is marked complete'),
  }));

  steps.push(mkStep('released', 'Crypto released (Done Trade)', orderStatus === 'completed', {
    who: task.platform_completed_by_username, at: task.platform_completed_at,
    hint: `Handler releases the crypto${handler ? ` — ${handler} clicks Done Trade` : ' (Done Trade)'}`,
  }));

  let terminal = null;
  if (orderStatus === 'cancelled') {
    const reason = task.platform_rejection_reason ? `Reason: ${task.platform_rejection_reason}` : null;
    if (approval === 'rejected') {
      terminal = { key: 'rejected', label: 'Rejected by handler', who: task.platform_approval_decided_by_username, at: task.platform_approval_decided_at, hint: reason };
    } else if (task.platform_approval_decided_by && (approval === 'revoked' || !approval)) {
      terminal = { key: 'revoked', label: 'Revoked by handler', who: task.platform_approval_decided_by_username, at: task.platform_approval_decided_at, hint: reason };
    } else {
      terminal = { key: 'cancelled', label: 'Order cancelled', at: task.completed_at, hint: task.platform_review_notes || null };
    }
  } else if (orderStatus === 'abandoned') {
    terminal = { key: 'abandoned', label: 'Trade abandoned', at: task.completed_at, hint: task.platform_review_notes ? `Reason: ${task.platform_review_notes}` : null };
  } else if (task.status === 'dismissed') {
    terminal = { key: 'withdrawn', label: 'Task withdrawn', at: task.completed_at };
  }
  return finalizeSteps(steps, terminal);
};

const approvalSteps = (task) => {
  const approval = task.platform_approval_status || task.workflow_stage;
  if (approval === 'rejected') {
    return finalizeSteps([
      mkStep('submitted', 'Submitted', true, { who: task.platform_submitted_by_username || task.created_by_username, at: task.platform_submitted_at || task.created_at }),
    ], { key: 'rejected', label: 'Rejected by handler', who: task.platform_approval_decided_by_username, at: task.platform_approval_decided_at, hint: task.platform_rejection_reason ? `Reason: ${task.platform_rejection_reason}` : null });
  }
  // The decision, then the order's own remaining steps — so the submitting
  // sales sees the whole life of the trade on this one card.
  return platformSteps(task, 'order');
};

// ---- PAYOUT (payment_request) ----
const paymentRequestSteps = (task) => {
  const done = task.status === 'completed';
  const slips = taskTranches(task);
  const paid = toNum(task.paid_total);
  const amount = toNum(task.amount);
  const prepared = taskFullyPrepared(task) || done;
  const last = latestBy(slips, 'paid_at');
  const completedSlips = slips.filter((s) => s.status === 'completed');
  const lastCompleted = latestBy(completedSlips, 'paid_at');
  return finalizeSteps([
    mkStep('requested', 'Requested', true, { who: task.created_by_username, at: task.created_at }),
    mkStep('claimed', 'Claimed by finance', !!task.claimed_by || done, {
      who: task.claimed_by_username, at: task.claimed_at, hint: 'Waiting for a finance user to claim it',
    }),
    mkStep('prepared', 'Prepared', prepared, {
      who: last?.prepared_by_name || last?.paid_by_name, at: last?.paid_at,
      detail: !prepared && paid > EPS
        ? `${formatCurrency(paid)} of ${formatCurrency(amount)} prepared`
        : (slips.length > 1 ? `${slips.length} slips` : null),
      hint: 'Finance prepares the payout: uploads the slip (no money moves yet)',
    }),
    mkStep('completed', 'Completed (bank debited)', done, {
      who: slipPayerText(slips), at: task.completed_at || lastCompleted?.paid_at,
      detail: !done && slips.length > 0 ? `${completedSlips.length} of ${slips.length} slips completed` : null,
      hint: `Finance marks each slip complete once the bank has debited ${ADMIN_CAN_COMPLETE_HINT}`,
    }),
  ], taskCancelledTerminal(task));
};

// ---- PAYMENT RECEIVED (payment_received) ----
const paymentReceivedSteps = (task) => {
  const done = task.status === 'completed';
  const progress = prReceiptProgress(task);
  const recorded = done || progress.allRecorded || (progress.total === 0 && prHasLiveRecord(task));
  const lastRec = latestBy(prReceipts(task).filter((r) => r.bank_transaction_id != null), 'recorded_at');
  return finalizeSteps([
    mkStep('submitted', 'Submitted', true, { who: task.created_by_username, at: task.created_at }),
    mkStep('claimed', 'Claimed by finance', !!task.claimed_by || done, {
      who: task.claimed_by_username, at: task.claimed_at, hint: 'Waiting for a finance user to claim it',
    }),
    mkStep('recorded', 'Recorded', recorded, {
      at: lastRec?.recorded_at || null,
      detail: progress.total > 1 ? `${progress.recorded} of ${progress.total} receipts recorded` : null,
      hint: 'Finance checks the receipt and records the bank credit it landed in',
    }),
    mkStep('completed', 'Completed', done, { at: task.completed_at }),
  ], taskCancelledTerminal(task));
};

// ---- EXPENSES ----
const EXPENSE_APPROVED = ['approved', 'paid', 'auto'];
const expenseTerminal = (task) => {
  const es = task.expense_status;
  if (es === 'rejected') {
    return { key: 'rejected', label: 'Rejected', who: task.expense_reviewed_by_username, at: task.expense_reviewed_at };
  }
  if (es === 'cancelled') return { key: 'cancelled', label: 'Claim cancelled', at: task.completed_at };
  return taskCancelledTerminal(task);
};

const expenseApprovalSteps = (task) => {
  const approved = EXPENSE_APPROVED.includes(task.expense_status) || (task.status === 'completed' && !task.expense_status);
  return finalizeSteps([
    mkStep('submitted', 'Submitted', true, { who: task.created_by_username, at: task.created_at }),
    mkStep('decision', 'Approved', approved, {
      who: task.expense_reviewed_by_username, at: task.expense_reviewed_at,
      hint: 'An administrator approves or rejects it on Expenses & Profit',
    }),
  ], expenseTerminal(task));
};

const expensePaymentSteps = (task) => {
  const done = task.status === 'completed' || task.expense_detail_status === 'completed';
  const slips = expenseSlips(task);
  const prepared = done || task.expense_detail_status === 'prepared' || slips.length > 0;
  const lastPrepared = latestBy(slips, 'prepared_at') || latestBy(slips, 'paid_at');
  const lastPaid = latestBy(slips.filter((s) => s.status === 'completed'), 'paid_at');
  const total = Number(task.expense_detail_total || 0);
  return finalizeSteps([
    mkStep('approved', 'Approved', true, { who: task.expense_reviewed_by_username, at: task.expense_reviewed_at }),
    mkStep('claimed', 'Claimed by finance', !!task.claimed_by || done, {
      who: task.claimed_by_username, at: task.claimed_at, hint: 'Waiting for a finance user to claim it',
    }),
    mkStep('prepared', 'Prepared', prepared, {
      who: lastPrepared?.prepared_by_name || lastPrepared?.paid_by_name, at: lastPrepared?.prepared_at || lastPrepared?.paid_at,
      detail: slips.length > 1 ? `${slips.length} slips` : null,
      hint: 'Finance prepares this payee’s payment on Expenses & Profit',
    }),
    mkStep('completed', 'Completed (paid)', done, {
      who: slipPayerText(slips), at: task.completed_at || lastPaid?.paid_at,
      detail: total > 1 ? `${Number(task.expense_detail_completed || 0)} of ${total} payees paid on this claim` : null,
      hint: `Finance marks it complete once the bank has debited ${ADMIN_CAN_COMPLETE_HINT}`,
    }),
  ], expenseTerminal(task));
};

// ---- NOTICES (pr_notification) ----
// Detected → Claimed → Handled → Done (operator 2026-10-12: a credit nobody has
// accounted for is claimed by whoever knows whose money it is — sales or
// finance — and only they act on it). `status` is the server's DERIVED notice
// status (it reads 'completed' the moment the credit is linked / assigned /
// classified / swap-marked, and 'pending' again if that is undone); the claim
// is the task's own claimed_by. Acting on an unclaimed notice claims it in the
// same step, so a handled notice always names who handled it.
const notificationSteps = (task) => {
  const done = task.status === 'completed';
  const holder = task.claimed_by_username || null;
  return finalizeSteps([
    mkStep('detected', 'Detected', true, { who: task.created_by_username, at: task.created_at }),
    mkStep('claimed', 'Claimed', !!task.claimed_by || done, {
      who: holder, at: task.claimed_at,
      hint: 'Waiting for sales or finance to claim it — whoever knows whose payment this is',
    }),
    mkStep('handled', 'Handled (linked / assigned / classified / swap-marked)', done, {
      who: done ? holder : null,
      hint: holder
        ? `${holder} links it to an order, assigns it to a customer, classifies it or marks it as a swap`
        : 'Link it to an order, assign it to a customer, classify it or mark it as a swap',
    }),
    mkStep('done', 'Done', done, { at: task.completed_at }),
  ], taskCancelledTerminal(task));
};

const genericSteps = (task) => {
  const done = task.status === 'completed';
  return finalizeSteps([
    mkStep('created', 'Created', true, { who: task.created_by_username, at: task.created_at }),
    mkStep('claimed', 'Claimed', !!task.claimed_by || done, { who: task.claimed_by_username, at: task.claimed_at, hint: 'Waiting for someone to claim it' }),
    mkStep('completed', 'Completed', done, { at: task.completed_at }),
  ], taskCancelledTerminal(task));
};

export const taskSteps = (task) => {
  if (!task) return [];
  switch (task.task_type) {
    case 'platform_order': return platformSteps(task, 'task');
    case 'platform_order_approval': return approvalSteps(task);
    case 'payment_request': return paymentRequestSteps(task);
    case 'payment_received': return paymentReceivedSteps(task);
    case 'expense_approval': return expenseApprovalSteps(task);
    case 'expense_payment': return expensePaymentSteps(task);
    case 'pr_notification': return notificationSteps(task);
    default: return genericSteps(task);
  }
};

// The compact reading of the same steps: where the task has got to (the last
// step of the unbroken done run), what is next (the current step's hint), and a
// tone for the chip colour. "Step 5 of 6 · Order paid".
export const taskStepSummary = (task) => {
  const steps = taskSteps(task);
  const total = steps.length;
  const cancelled = steps.find((s) => s.state === 'cancelled') || null;
  const currentIndex = steps.findIndex((s) => s.state === 'current');
  const current = currentIndex >= 0 ? steps[currentIndex] : null;
  let reachedIndex;
  if (cancelled) reachedIndex = steps.indexOf(cancelled);
  else if (currentIndex === -1) reachedIndex = total - 1;
  else reachedIndex = Math.max(0, currentIndex - 1);
  const reached = steps[reachedIndex] || null;
  const doneCount = steps.filter((s) => s.state === 'done').length;
  let text;
  if (cancelled) text = cancelled.who ? `${cancelled.label} · ${cancelled.who}` : cancelled.label;
  else text = `Step ${reachedIndex + 1} of ${total} · ${reached ? reached.label : ''}`;
  return {
    steps,
    total,
    doneCount,
    reached,
    reachedIndex,
    current,
    cancelled,
    tone: cancelled ? 'cancelled' : (current ? 'progress' : 'done'),
    text,
    hint: cancelled ? cancelled.hint : (current ? current.hint : null),
  };
};
